import ToggleButton from 'components/Buttons/ToggleButton';
import BaseCard from 'components/Cards/BaseCard';
import { CARD_CLASS_NAMES } from 'features/Integrations/features/ConnectedDevices/features/FloodDeviceSettings/config/floodDeviceConfig';
import React from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa6';
import { getIndex } from '../../../../types/guards';
import type { PermItem } from '../../../../types/Roles.types';

// A permission group is a header (linked > 0) plus the child permissions it
// controls, OR a run of standalone permissions (header === null). Groups render
// as self-contained cards that reflow across the responsive grid. Children may
// themselves be `PermGroup`s to support nested subgrouping.
export interface PermGroup {
  key: string;
  header: PermItem | null;
  children: Array<PermItem | PermGroup>;
}

const normalizeSearchText = (value: string): string => value.toLowerCase().replace(/ /g, '');

// (Grouping is provided by the curated util `groupPerms.ts`.)

// Collect collapsible header keys recursively so nested subgroup headers are
// included alongside top-level section headers.
export const collectHeaderKeys = (gs: PermGroup[]): string[] => {
  const keys: string[] = [];
  const recurse = (g: PermGroup) => {
    if (g.header) keys.push(g.key);
    g.children.forEach((child) => {
      if (child && typeof child === 'object' && (child as PermGroup).children !== undefined) {
        recurse(child as PermGroup);
      }
    });
  };
  gs.forEach((g) => recurse(g));
  return keys;
};

interface PermissionsDisplayProps {
  groups: PermGroup[];
  search: string;
  collapsedGroups: Record<string, boolean>;
  setCollapsedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  // Toggle math is owned by the form container; the display asks for the
  // rendered state and change handler per permission item.
  getToggleState: (item: PermItem) => { isOff: boolean; onToggle: () => void };
}

const PermissionsDisplay: React.FC<PermissionsDisplayProps> = ({
  groups,
  search,
  collapsedGroups,
  setCollapsedGroups,
  getToggleState,
}) => {
  const query = normalizeSearchText(search);
  const matchesSearch = (item: PermItem): boolean => {
    if (!query) return true;
    return (
      normalizeSearchText(item.display ?? '').includes(query) ||
      normalizeSearchText(item.name).includes(query) ||
      normalizeSearchText(item.desc ?? '').includes(query)
    );
  };

  // Renders a single permission row. When a collapseKey is passed (header rows),
  // the label doubles as a collapse toggle for the card's children; the master
  // ToggleButton stays independent of that collapse control.
  const renderPermRow = (
    item: PermItem,
    opts: { isHeader?: boolean; collapseKey?: string } = {},
  ): React.ReactNode => {
    const { isHeader = false, collapseKey } = opts;
    const { isOff, onToggle } = getToggleState(item);
    const idx = getIndex(item) ?? -1;
    const isCollapsed = collapseKey ? Boolean(collapsedGroups[collapseKey]) : false;

    const label = (
      <div className="min-w-0 text-start" style={{ color: 'var(--color-font)' }}>
        <div className={isHeader ? 'fw-bold fs-16' : 'fw-semibold'}>
          {item.display ? item.display : item.name}
        </div>
        {item.desc ? <div className="text-secondary small">{item.desc}</div> : null}
      </div>
    );

    return (
      <div
        key={idx >= 0 ? idx : item.name}
        className="d-flex flex-row justify-content-between align-items-center gap-3 py-2"
      >
        {collapseKey ? (
          <button
            type="button"
            onClick={() =>
              setCollapsedGroups((prev) => ({ ...prev, [collapseKey]: !prev[collapseKey] }))
            }
            aria-expanded={isCollapsed ? false : true}
            className="btn btn-link p-0 text-decoration-none text-white d-flex flex-row align-items-center gap-2 min-w-0 flex-grow-1"
          >
            {isCollapsed ? (
              <FaChevronRight className="flex-shrink-0" />
            ) : (
              <FaChevronDown className="flex-shrink-0" />
            )}
            {label}
          </button>
        ) : (
          label
        )}
        <div
          className="flex-shrink-0 d-flex align-items-center"
          onClickCapture={(e: React.MouseEvent) => {
            if (!collapseKey) return;
            const target = e.target as HTMLElement | null;
            // If the click originated from the toggle button (or its children),
            // don't toggle collapse — let the ToggleButton handle the event.
            if (
              target &&
              (target.closest?.('.toggle-button-group') || target.closest?.('.toggle-button'))
            ) {
              return;
            }
            setCollapsedGroups((prev) => ({ ...prev, [collapseKey]: !prev[collapseKey] }));
          }}
        >
          <ToggleButton isOff={isOff} onClick={onToggle} />
        </div>
      </div>
    );
  };

  // Determine whether a node (PermItem or PermGroup) or any of its descendants
  // match the current search query.
  const nodeMatchesSearch = (node: PermItem | PermGroup): boolean => {
    if ((node as PermGroup).header) {
      const g = node as PermGroup;
      if (g.header && matchesSearch(g.header)) return true;
      return g.children.some((c) => nodeMatchesSearch(c as PermItem | PermGroup));
    }
    return matchesSearch(node as PermItem);
  };

  // Render a child node recursively with indentation for depth.
  const renderChildNode = (node: PermItem | PermGroup, depth = 1): React.ReactNode => {
    if ((node as PermGroup).header) {
      const g = node as PermGroup;
      const isCollapsed = Boolean(collapsedGroups[g.key]);
      return (
        <div key={g.key} className="d-flex flex-column">
          {renderPermRow(g.header as PermItem, { isHeader: true, collapseKey: g.key })}
          {!isCollapsed && g.children.length > 0 && (
            <div style={{ paddingLeft: depth * 12 }}>
              {g.children.map((c) => renderChildNode(c as PermItem | PermGroup, depth + 1))}
            </div>
          )}
        </div>
      );
    }
    const item = node as PermItem;
    return (
      <div key={getIndex(item) ?? item.name} style={{ paddingLeft: depth * 12 }}>
        {renderPermRow(item, { isHeader: false })}
      </div>
    );
  };

  return (
    <div
      className="customScrollbar-Y overflow-y-auto overflow-x-hidden flex-grow-1 px-3 pt-3"
      style={{ minHeight: 0 }}
    >
      <div className="row g-3">
        {groups.map((group) => {
          const headerMatches = group.header ? matchesSearch(group.header) : false;
          // When the header itself matches, keep the whole group intact for
          // context; otherwise only surface the children (or subgroups) that
          // matched the search.
          const childrenToShow = headerMatches
            ? group.children
            : group.children.filter((c) => nodeMatchesSearch(c as PermItem | PermGroup));

          if (!headerMatches && childrenToShow.length === 0) return null;

          // Only header cards collapse; standalone cards have no header to click.
          const isCollapsed = Boolean(group.header) && Boolean(collapsedGroups[group.key]);
          // When collapsed, remove Card.Body padding to avoid extra blank space below header
          const bodyStyle = isCollapsed ? { padding: 0 } : undefined;

          return (
            <div key={group.key} className="col-12 col-md-6 col-xxl-4 d-flex">
              <BaseCard
                className={`shadow-sm w-100 d-flex flex-column ${CARD_CLASS_NAMES.card}`}
                header={
                  group.header
                    ? renderPermRow(group.header as PermItem, {
                        isHeader: true,
                        collapseKey: group.key,
                      })
                    : undefined
                }
                headerClassName={CARD_CLASS_NAMES.cardHeader}
                bodyClassName={`d-flex flex-column ${CARD_CLASS_NAMES.cardBody}`}
                bodyStyle={bodyStyle}
              >
                {!isCollapsed && (
                  <div className="d-flex flex-column">
                    {childrenToShow.map((child) => {
                      // child may be a PermItem or a nested PermGroup
                      return (child as PermGroup).header
                        ? renderChildNode(child as PermGroup, 1)
                        : renderPermRow(child as PermItem, { isHeader: false });
                    })}
                  </div>
                )}
              </BaseCard>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionsDisplay;
